import { type FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Icon, Modal, Textarea, type Column } from "@legal-500/legal500-design-system";
import { powerlistQuestions, type Question } from "../../api/powerlist.service";
import { DataTable } from "../../components/DataTable/DataTable";
import { ErrorBanner } from "../../components/ErrorBanner/ErrorBanner";
import LoadingState from "../../components/LoadingState";
import { PageHeader } from "../../components/PageHeader";
import { useQuery } from "../../hooks/useApi";
import { ROUTES } from "../../routes";
import { ConfirmModal, FormError, errorMessage, usePowerlistAccess, useToast } from "./shared";
import styles from "./Powerlist.module.css";

type QuestionFormModalProps = {
  isOpen: boolean;
  question: Question | null;
  onClose: () => void;
  onSaved: () => void;
};

function QuestionFormModal({ isOpen, question, onClose, onSaved }: QuestionFormModalProps) {
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setText(question?.text ?? "");
    setError(null);
    setSaving(false);
  }, [isOpen, question]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!text.trim()) {
      setError("Question text is required");
      return;
    }
    setSaving(true);
    try {
      if (question) await powerlistQuestions.update(question.id, { text: text.trim() });
      else await powerlistQuestions.create({ text: text.trim() });
      onSaved();
    } catch (saveError) {
      setError(errorMessage(saveError));
      setSaving(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={question ? "Edit question" : "New question"}>
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <FormError message={error} />
        <Textarea
          id="powerlist-question-text"
          label="Question"
          value={text}
          onChange={event => setText(event.target.value)}
          rows={3}
          fullWidth
        />
        <div className={styles.formActions}>
          <Button variant="outlined" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={saving}>
            {saving ? "Saving..." : question ? "Save" : "Create question"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

type FormState = { open: false } | { open: true; question: Question | null };

export default function QuestionBankPage() {
  const navigate = useNavigate();
  const { canWrite, canDelete } = usePowerlistAccess();
  const { data, loading, error, refetch, isRefetching } = useQuery(
    () => powerlistQuestions.list(),
    [],
  );
  const [form, setForm] = useState<FormState>({ open: false });
  const [removing, setRemoving] = useState<Question | null>(null);
  const { toast, success, failure } = useToast();
  const questions = [...(data ?? [])].sort(
    (a, b) => Number(b.isActive) - Number(a.isActive) || a.text.localeCompare(b.text),
  );

  async function setActive(question: Question, isActive: boolean) {
    try {
      await powerlistQuestions.update(question.id, { isActive });
      success(isActive ? "Question activated" : "Question deactivated");
    } catch (updateError) {
      failure(updateError);
    } finally {
      await refetch();
    }
  }

  const columns: Column<Question>[] = [
    { header: "Question", accessor: "text", wrap: true },
    {
      header: "Status",
      accessor: "isActive",
      render: (_value: unknown, row: Question) => (
        <span className={styles.tag}>{row.isActive ? "Active" : "Inactive"}</span>
      ),
    },
    ...(canWrite || canDelete
      ? [
          {
            header: "Actions",
            accessor: "id" as const,
            render: (_value: unknown, row: Question) => (
              <div className={styles.actions}>
                {canWrite && (
                  <Button
                    variant="outlined"
                    size="small"
                    aria-label={`Edit "${row.text}"`}
                    onClick={() => setForm({ open: true, question: row })}
                  >
                    Edit
                  </Button>
                )}
                {canWrite && (
                  <Button
                    variant="outlined"
                    size="small"
                    aria-label={`${row.isActive ? "Deactivate" : "Activate"} "${row.text}"`}
                    onClick={() => void setActive(row, !row.isActive)}
                  >
                    {row.isActive ? "Deactivate" : "Activate"}
                  </Button>
                )}
                {canDelete && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    aria-label={`Delete "${row.text}"`}
                    onClick={() => setRemoving(row)}
                  >
                    Delete
                  </Button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className={styles.wrapper}>
      <PageHeader title="Question bank" subtitle="Interview questions editions can choose from">
        <div className={styles.headerActions}>
          <Button
            variant="outlined"
            color="default"
            size="small"
            onClick={() => navigate(ROUTES.powerlist.series)}
          >
            <Icon icon="arrow-left" size="small" />
            All series
          </Button>
          {canWrite && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setForm({ open: true, question: null })}
            >
              New question
            </Button>
          )}
        </div>
      </PageHeader>
      {error && <ErrorBanner error={error.message} onRetry={refetch} loading={isRefetching} />}
      {loading && data === null ? (
        <LoadingState message="Loading questions..." />
      ) : (
        <div className={styles.tableContainer}>
          <DataTable
            data={questions}
            columns={columns}
            rowKey="id"
            emptyMessage="The question bank is empty."
          />
        </div>
      )}
      <QuestionFormModal
        isOpen={form.open}
        question={form.open ? form.question : null}
        onClose={() => setForm({ open: false })}
        onSaved={() => {
          setForm({ open: false });
          success("Question saved");
          void refetch();
        }}
      />
      <ConfirmModal
        isOpen={removing !== null}
        title="Delete question"
        message="Delete this question? A question with answers can only be deactivated."
        confirmLabel="Delete"
        onClose={() => setRemoving(null)}
        onConfirm={async () => {
          if (!removing) return;
          await powerlistQuestions.remove(removing.id);
          setRemoving(null);
          success("Question deleted");
          await refetch();
        }}
      />
      {toast}
    </div>
  );
}
